import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Code, Globe, Users } from 'lucide-react';

interface SetupStepsProps {
  onSetupComplete?: () => void;
}

const SetupSteps: React.FC<SetupStepsProps> = ({ onSetupComplete }) => {
  const navigate = useNavigate();
  const [setupState, setSetupState] = useState({
    usersImported: false,
    sdkSetup: false,
    domainVerified: false,
  });

  const setupSteps = [
    {
      id: 'import-users',
      title: 'Import Users',
      description: 'Upload your user data to start tracking engagement patterns.',
      completed: setupState.usersImported,
      icon: Users,
      action: () => {
        navigate('/members/import');
        setSetupState((prev) => ({ ...prev, usersImported: true }));
      },
      actionText: 'Import Users',
    },
    {
      id: 'setup-sdk',
      title: 'Setup SDK',
      description: 'Integrate our tracking SDK into your application codebase.',
      completed: setupState.sdkSetup,
      icon: Code,
      action: () => {
        setSetupState((prev) => ({ ...prev, sdkSetup: true }));
      },
      actionText: 'Setup SDK',
    },
    {
      id: 'verify-domain',
      title: 'Verify Domain',
      description: 'Confirm domain ownership for secure data collection.',
      completed: setupState.domainVerified,
      icon: Globe,
      action: () => {
        setSetupState((prev) => ({ ...prev, domainVerified: true }));
      },
      actionText: 'Verify Domain',
    },
  ];

  const completedSteps = setupSteps.filter((step) => step.completed).length;
  const progressPercentage = (completedSteps / setupSteps.length) * 100;
  const allStepsCompleted = completedSteps === setupSteps.length;

  // Call parent callback when setup is complete
  React.useEffect(() => {
    if (allStepsCompleted && onSetupComplete) {
      onSetupComplete();
    }
  }, [allStepsCompleted, onSetupComplete]);

  return (
    <div className="max-w-6xl mx-auto p-8">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4" style={{ color: '#fafafa' }}>
          Complete Your Setup
        </h1>
        <p className="text-lg mb-8 max-w-2xl mx-auto" style={{ color: '#9ca3af' }}>
          Get started by completing these steps - you can always modify them later!
        </p>

        {/* Progress */}
        <div
          className="max-w-md mx-auto border rounded-lg p-4 shadow-sm"
          style={{ backgroundColor: '#1f2937', borderColor: '#374151' }}
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium" style={{ color: '#9ca3af' }}>
              Setup Progress
            </span>
            <span className="text-sm font-semibold">
              <span style={{ color: '#00b9ff' }}>{completedSteps}</span>
              <span style={{ color: '#9ca3af' }}> / {setupSteps.length}</span>
            </span>
          </div>
          <div className="w-full rounded-full h-2" style={{ backgroundColor: '#374151' }}>
            <div
              className="h-2 rounded-full transition-all duration-300"
              style={{ backgroundColor: '#00b9ff', width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        {setupSteps.map((step, index) => {
          const Icon = step.icon;

          return (
            <div
              key={step.id}
              className={`
                border rounded-lg p-8 text-center transition-all duration-200 hover:shadow-md min-h-96 flex flex-col justify-center
                ${step.completed ? 'shadow-sm' : ''}
              `}
              style={{
                backgroundColor: step.completed ? 'rgba(34, 197, 94, 0.1)' : '#1f2937',
                borderColor: step.completed ? 'rgba(34, 197, 94, 0.3)' : '#374151',
              }}
            >
              {/* Icon */}
              <div
                className="w-20 h-20 mx-auto mb-6 rounded-lg flex items-center justify-center"
                style={{
                  backgroundColor: step.completed ? 'rgba(34, 197, 94, 0.2)' : '#374151',
                }}
              >
                {step.completed ? (
                  <CheckCircle className="w-10 h-10" style={{ color: '#22c55e' }} />
                ) : (
                  <Icon className="w-10 h-10" style={{ color: '#9ca3af' }} />
                )}
              </div>

              {/* Content */}
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#fafafa' }}>
                {step.title}
              </h3>
              <p className="mb-8 leading-relaxed flex-grow" style={{ color: '#9ca3af' }}>
                {step.description}
              </p>

              {/* Action */}
              {step.completed ? (
                <div className="flex items-center justify-center space-x-2" style={{ color: '#22c55e' }}>
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-medium">Complete</span>
                </div>
              ) : (
                <button
                  onClick={step.action}
                  className="w-full py-3 px-4 rounded-lg font-medium transition-colors hover:brightness-90"
                  style={{ backgroundColor: '#00b9ff', color: '#fafafa' }}
                >
                  {step.actionText}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Success Message */}
      {allStepsCompleted && (
        <div
          className="border rounded-lg p-8 text-center mb-12"
          style={{
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            borderColor: 'rgba(34, 197, 94, 0.3)',
          }}
        >
          <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#22c55e' }} />
          <h3 className="text-2xl font-bold mb-3" style={{ color: '#fafafa' }}>
            Setup Complete!
          </h3>
          <p className="text-lg" style={{ color: '#9ca3af' }}>
            Excellent! Your churn prevention system is now active and ready to help you retain customers.
          </p>
        </div>
      )}
    </div>
  );
};

export default SetupSteps;
